import { AgGridAngular } from '@ag-grid-community/angular';
import { ColDef, Column, SortChangedEvent } from '@ag-grid-community/core';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  FrkBreakpoint,
  HeaderRendererComponent,
  ResponsiveService,
} from '@frk/eds-components';
import { UsCapitalGainsEstimatesService } from '@products/tax/services/us-capital-gains-estimates.service';
import {
  CapGainsEstimatesColumnId,
  CapGainsEstimatesColumnSortDetails,
  CapGainsEstimatesColumnSortType,
  CapGainsEstimatesListing,
  CapGainsEstimatesRowSelection,
} from '@products/types/tax.type';
import { TranslateService } from '@shared/translate/translate.service';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FundNameCellRendererComponent } from './ag-grid-helpers/fund-name-cell-renderer/fund-name-cell-renderer.component';
import { FundSelectAllHeaderRendererComponent } from './ag-grid-helpers/fund-select-all-header-renderer/fund-select-all-header-renderer.component';
import { FundSelectCellRendererComponent } from './ag-grid-helpers/fund-select-cell-renderer/fund-select-cell-renderer.component';
@Component({
  selector: 'ft-us-capital-gains-estimates-listing',
  templateUrl: './us-capital-gains-estimates-listing.component.html',
  styleUrls: ['./us-capital-gains-estimates-listing.component.scss'],
})
export class UsCapitalGainsEstimatesListingComponent
  implements OnInit, OnChanges, OnDestroy {
  @Input() gridListing: CapGainsEstimatesListing[] = [];
  @Input() totalSelectedRows: number;
  @Input() gridRowsCount: number;
  @Output()
  updateNodeSelection = new EventEmitter<CapGainsEstimatesRowSelection>();
  @Output() sortColumn = new EventEmitter<CapGainsEstimatesColumnSortDetails>();
  isHandheld$: Observable<boolean>;

  private unsubscribe$: Subject<void> = new Subject<void>();
  // ***** AG-Grid related declarations *****
  gridContext = {};
  frameworkComponents = {
    fundNameCellRendererComponent: FundNameCellRendererComponent,
    fundSelectCellRendererComponent: FundSelectCellRendererComponent,
    fundSelectAllHeaderRendererComponent: FundSelectAllHeaderRendererComponent,
  };
  defaultColDef: ColDef = {
    wrapText: false,
    width: 160,
    autoHeight: true,
    sortable: true,
    // Although custom sorting has to handled on service yet 'comparator' is needed to get 'SortChangedEvent' by ag-grid.
    // And, returned '0', as we don't want default sorting to be applied by ag-grid.
    comparator: (value1, value2, nodeA, nodeB, isInverted) => {
      return 0;
    },
  };
  columnDefs: ColDef[] = [];
  sortedTableRows: CapGainsEstimatesListing[] = [];
  isSelectAllCheckboxChecked = false;
  totalTableRowsChecked: number;
  totalTableRowsCount: number;
  @Input() asOfDate: string;
  hideNoEstimateFunds: boolean;
  @ViewChild('estimateGrid', { read: AgGridAngular })
  estimateGrid: AgGridAngular;
  isDesktop: boolean;

  constructor(
    private translateService: TranslateService,
    private cdr: ChangeDetectorRef,
    private responsiveService: ResponsiveService,
    private usCapitalGainsEstimatesService: UsCapitalGainsEstimatesService
  ) {}

  ngOnInit(): void {
    this.isHandheld$ = this.responsiveService.isHandheld$();
    this.responsiveService
      .getBreakpointObs$()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((bp: FrkBreakpoint) => {
        if ([FrkBreakpoint.Desktop].includes(bp)) {
          this.isDesktop = true;
        }
      });
    this.usCapitalGainsEstimatesService
      .getHideFundEstimateFlag$()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((flag) => {
        this.hideNoEstimateFunds = flag;
        this.cdr.detectChanges();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.gridListing?.currentValue) {
      this.sortedTableRows = changes.gridListing.currentValue;
      if (this.hideNoEstimateFunds) {
        this.sortedTableRows = this.usCapitalGainsEstimatesService.getFundEstimates(
          this.sortedTableRows
        );
      }
      this.columnDefs = this.getColumnDefs();

      this.cdr.detectChanges();

      // Make row selected for each record which has 'isSelected' as true.
      this.estimateGrid?.api.forEachNode((node) =>
        node.setSelected((node.data as CapGainsEstimatesListing).isSelected)
      );
    }

    if (changes.totalSelectedRows?.currentValue) {
      this.totalTableRowsChecked = changes.totalSelectedRows.currentValue;
    }

    if (changes.gridRowsCount?.currentValue) {
      this.totalTableRowsCount = changes.gridRowsCount?.currentValue;
    }

    // For any filter(s) applied, if every row has checkbox selected,
    // Then make, "select all" checkbox to checked as well. Else, make it unchecked.
    if (this.sortedTableRows.every((tableRow) => tableRow.isSelected)) {
      this.isSelectAllCheckboxChecked = true;
    } else {
      this.isSelectAllCheckboxChecked = false;
    }

    // And, do refresh the header, to make "select all" checkbox consistent with current state.
    this.estimateGrid?.api.refreshHeader();
  }

  /**
   * Prepare the column definitions and create a list.
   * @returns column definition list
   */
  private getColumnDefs(): ColDef[] {
    return [
      {
        colId: CapGainsEstimatesColumnId.FUND_SELECT,
        headerComponent: 'fundSelectAllHeaderRendererComponent',
        headerComponentParams: {
          parentComponent: this,
        },
        cellRenderer: 'fundSelectCellRendererComponent',
        cellRendererParams: {
          parentComponent: this,
        },
        sortable: false,
        width: 30,
      },
      {
        colId: CapGainsEstimatesColumnId.FUND_NAME,
        headerName: this.translateService.instant(
          'products.capgains-table-heading-fund-name'
        ),
        headerComponentFramework: HeaderRendererComponent,
        valueGetter: (params) => {
          return (params.data as CapGainsEstimatesListing).fundName;
        },
        cellRenderer: 'fundNameCellRendererComponent',
        wrapText: true,
        minWidth: 250,
        flex: 1,
      },
      {
        colId: CapGainsEstimatesColumnId.TICKER,
        headerName: this.translateService.instant(
          'products.capgains-table-heading-ticker'
        ),
        headerComponentFramework: HeaderRendererComponent,
        valueGetter: (params) => {
          return (params.data as CapGainsEstimatesListing).ticker;
        },
        width: 80,
        wrapText: true,
      },

      {
        colId: CapGainsEstimatesColumnId.SHARE_CLASS,
        headerName: this.translateService.instant(
          'products.capgains-table-heading-share-class'
        ),
        headerComponentFramework: HeaderRendererComponent,
        valueGetter: (params) => {
          return (params.data as CapGainsEstimatesListing).shareClassName;
        },
        width: 120,
        wrapText: true,
      },

      {
        colId: CapGainsEstimatesColumnId.LONG_TERM_CAP_GAINS_PERCENT,
        headerName: this.translateService.instant(
          'products.capgains-table-heading-long-term-cap-gains-perc'
        ),
        headerComponentParams: {
          isAlignRight: true,
        },
        cellClass: [
          'ag-cell-frk--highlighted-secondary',
          'ag-cell-frk--monetary',
          'ag-cell-frk--break-word',
        ],
        headerComponentFramework: HeaderRendererComponent,
        valueGetter: (params) => {
          const record: CapGainsEstimatesListing = params.data;
          return record.actualsAlert
            ? this.translateService.instant('products.capgains-actualsposted')
            : record.longTermCapitalGainPercent;
        },
        width: 140,
      },
      {
        colId: CapGainsEstimatesColumnId.SHORT_TERM_CAP_GAINS_PERCENT,
        headerName: this.translateService.instant(
          'products.capgains-table-heading-short-term-cap-gains-perc'
        ),
        cellClass: [
          'ag-cell-frk--highlighted-secondary',
          'ag-cell-frk--monetary',
          'ag-cell-frk--break-word',
        ],
        headerComponentFramework: HeaderRendererComponent,
        valueGetter: (params) => {
          const record: CapGainsEstimatesListing = params.data;
          return record.actualsAlert
            ? this.translateService.instant('products.capgains-actualsposted')
            : record.shortTermCapitalGainPercent;
        },
        headerComponentParams: {
          isAlignRight: true,
        },
        width: 140,
      },
      {
        colId: CapGainsEstimatesColumnId.TOTAL_PERCENT,
        headerName: this.translateService.instant(
          'products.capgains-table-heading-total-percent'
        ),
        headerComponentParams: {
          isAlignRight: true,
        },
        headerComponentFramework: HeaderRendererComponent,
        cellClass: [
          'ag-cell-frk--highlighted-secondary',
          'ag-cell-frk--monetary',
          'ag-cell-frk--break-word',
        ],
        valueGetter: (params) => {
          const record: CapGainsEstimatesListing = params.data;
          return record.actualsAlert
            ? this.translateService.instant('products.capgains-actualsposted')
            : record.totalGainNav;
        },
        width: 120,
      },

      {
        colId: CapGainsEstimatesColumnId.LONG_TERM_CAP_GAINS,
        headerName:
          this.translateService.instant(
            'products.capgains-excel-headers-long-term-gain'
          ) +
          '<br/>' +
          this.translateService.instant(
            'products.capgains-excel-headers-cap-gains'
          ),
        headerComponentParams: {
          isAlignRight: true,
        },
        headerComponentFramework: HeaderRendererComponent,
        valueGetter: (params) => {
          const record: CapGainsEstimatesListing = params.data;

          return record.actualsAlert
            ? this.translateService.instant('products.capgains-actualsposted')
            : record.longTermCapitalGainAmount;
        },

        width: 165,
        cellClass: ['ag-cell-frk--monetary', 'ag-cell-frk--break-word'],
      },
      {
        colId: CapGainsEstimatesColumnId.SHORT_TERM_CAP_GAINS,
        headerName:
          this.translateService.instant(
            'products.capgains-excel-headers-short-term-gain'
          ) +
          '<br/>' +
          this.translateService.instant(
            'products.capgains-excel-headers-cap-gains'
          ),
        headerComponentParams: {
          isAlignRight: true,
        },
        headerComponentFramework: HeaderRendererComponent,
        valueGetter: (params) => {
          const record: CapGainsEstimatesListing = params.data;

          return record.actualsAlert
            ? this.translateService.instant('products.capgains-actualsposted')
            : record.shortTermCapitalGainAmount;
        },
        width: 165,
        cellClass: ['ag-cell-frk--monetary', 'ag-cell-frk--break-word'],
      },
      {
        colId: CapGainsEstimatesColumnId.NAV_AS_OF,
        headerName:
          this.translateService.instant('products.NAV_VALUE') +
          '<br/>' +
          this.translateService.instant(
            'products.capgains-table-heading-nav-as-of'
          ) +
          ' ' +
          this.asOfDate,
        headerComponentFramework: HeaderRendererComponent,
        headerComponentParams: {
          isAlignRight: true,
        },
        cellClass: [
          'ag-cell-frk--highlighted-secondary',
          'ag-cell-frk--monetary',
        ],
        valueGetter: (params) => {
          return (params.data as CapGainsEstimatesListing).navValue;
        },
        wrapText: true,
        width: 140,
      },
      {
        colId: CapGainsEstimatesColumnId.RECORD_DATE,
        headerName: this.translateService.instant('pds.INCM_DIST_RECORD_DATE'),
        headerComponentFramework: HeaderRendererComponent,
        headerComponentParams: {
          isAlignRight: true,
        },
        cellClass: [
          'ag-cell-frk--highlighted-secondary',
          'ag-cell-frk--monetary',
        ],
        valueGetter: (params) => {
          return (params.data as CapGainsEstimatesListing)
            .capitalGainRecordDate;
        },
        width: 110,
      },
      {
        colId: CapGainsEstimatesColumnId.EX_DATE,
        headerName: this.translateService.instant(
          'products.capgains-table-heading-ex-date'
        ),
        headerComponentParams: {
          isAlignRight: true,
        },
        cellClass: [
          'ag-cell-frk--highlighted-secondary',
          'ag-cell-frk--monetary',
        ],
        headerComponentFramework: HeaderRendererComponent,
        valueGetter: (params) => {
          return (params.data as CapGainsEstimatesListing)
            .capitalGainExDividendDate;
        },
        width: 110,
        wrapText: true,
      },
      {
        colId: CapGainsEstimatesColumnId.EX_DATE,
        headerName: this.translateService.instant(
          'products.capgains-table-heading-payable-date'
        ),
        headerComponentParams: {
          isAlignRight: true,
        },
        cellClass: [
          'ag-cell-frk--highlighted-secondary',
          'ag-cell-frk--monetary',
        ],
        headerComponentFramework: HeaderRendererComponent,
        valueGetter: (params) => {
          return (params.data as CapGainsEstimatesListing)
            .capitalGainPayableDate;
        },
        width: 110,
        wrapText: true,
      },
    ];
  }

  /**
   * Trigger row selection(s) as per checkbox(es) clicked.
   * @param rowSelection 'flag' value with / without record 'id'
   */
  onRowSelected(rowSelection: CapGainsEstimatesRowSelection): void {
    this.updateNodeSelection.next(rowSelection);
  }

  /**
   * Trigger sort on the target column as per ag grid event received.
   * @param event ag grid sort changed event
   */
  onSortChanged(event: SortChangedEvent) {
    const gridCols: Column[] = event.columnApi.getAllDisplayedColumns();
    let sortedColId: string;
    let colSortType: string;

    // Loop every grid column and check for any sort info.
    // Basically, "getSort()" will return 'asc' or 'desc', if sort applied.
    // Othwerwise, 'undefined'.
    gridCols.find((gridCol) => {
      if (gridCol.getSort()) {
        sortedColId = gridCol.getColId();
        colSortType = gridCol.getSort();
      }
    });

    // Pass the extracted column info to service for sort operations.
    this.sortColumn.next({
      columnId: sortedColId as CapGainsEstimatesColumnId,
      sortType: colSortType as CapGainsEstimatesColumnSortType,
    });
  }

  /**
   * ********** Set external filter ag-grid configurations **********
   */

  isExternalFilterPresent = (): boolean => true;

  doesExternalFilterPass(node: any): boolean {
    if (node.gridOptionsWrapper.getContext().filteredRows) {
      return (
        node.gridOptionsWrapper.getContext().filteredRows.indexOf(node.data) >
        -1
      );
    }
    return true;
  }
  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
